import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { apiService } from '../services/api';

// Device and Session Information
interface DeviceInfo {
  id: string;
  name: string;
  type: string; // 'web', 'mobile', 'desktop', 'cli'
  browser?: string; // For web sessions
  os?: string; // Operating system
  last_seen: string;
  created_at?: string;
  last_activated?: string;
  is_current: boolean;
}

interface SessionInfo {
  id: string;
  device_id: string;
  device_name: string;
  device_type: string; // 'web', 'mobile', 'desktop'
  created_at: string;
  last_used: string;
  expires_at: string;
  is_active: boolean;
  is_current: boolean;
  ip_address?: string;
}

// User Profile Information
interface UserProfile {
  id: string;
  email: string;
  name: string;
  role: string;
  mode?: string; // "full" or "reader"
  is_verified: boolean;
  created_at: string;
  updated_at: string;
  last_login?: string;
  preferences?: {
    theme: 'light' | 'dark' | 'auto';
    language: string;
    notifications: {
      email: boolean;
      push: boolean;
      security_alerts: boolean;
    };
  };
}

// User Context Type
interface UserContextType {
  // User Profile
  profile: UserProfile | null;
  
  // Device & Session Management
  currentDevice: DeviceInfo | null;
  currentSession: SessionInfo | null;
  devices: DeviceInfo[];
  sessions: SessionInfo[];
  
  // User Actions
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;
  updatePreferences: (preferences: Partial<UserProfile['preferences']>) => Promise<void>;
  getDevices: () => Promise<void>;
  getSessions: () => Promise<void>;
  revokeDevice: (deviceId: string) => Promise<void>;
  revokeSession: (sessionId: string) => Promise<void>;
  
  // Loading States
  isLoading: boolean;
  isUpdatingProfile: boolean;
  isManagingDevices: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  
  // State
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [currentDevice, setCurrentDevice] = useState<DeviceInfo | null>(null);
  const [currentSession, setCurrentSession] = useState<SessionInfo | null>(null);
  const [devices, setDevices] = useState<DeviceInfo[]>([]);
  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  
  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [isManagingDevices, setIsManagingDevices] = useState(false);

  // Initialize user data when authentication changes
  const initializeUserData = useCallback(async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      // Set profile from auth user data
      setProfile({
        id: user.id,
        email: user.email,
        name: user.name,
        role: user.role,
        mode: user.mode || 'full',
        is_verified: user.is_verified,
        created_at: user.created_at || new Date().toISOString(),
        updated_at: user.updated_at || new Date().toISOString(),
        last_login: new Date().toISOString(),
        preferences: {
          theme: 'light',
          language: 'en',
          notifications: {
            email: true,
            push: true,
            security_alerts: true,
          },
        },
      });

      // Set current device and session from auth data
      if (user.device_id && user.device_name && user.session_type) {
        const deviceInfo: DeviceInfo = {
          id: user.device_id,
          name: user.device_name,
          type: user.session_type,
          browser: user.session_type === 'web' ? 'Chrome Browser' : undefined,
          last_seen: new Date().toISOString(),
          is_current: true,
        };
        
        const sessionInfo: SessionInfo = {
          id: `${user.device_id}-${Date.now()}`,
          device_id: user.device_id,
          device_name: user.device_name || 'Web Browser',
          device_type: user.session_type,
          created_at: new Date().toISOString(),
          last_used: new Date().toISOString(),
          expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24 hours from now
          is_active: true,
          is_current: true,
        };
        
        setCurrentDevice(deviceInfo);
        setCurrentSession(sessionInfo);
      }

      // Load additional devices and sessions
      await Promise.all([
        getDevices(),
        getSessions(),
      ]);
    } catch (error) {
      console.error('Failed to initialize user data:', error);
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (isAuthenticated && user) {
      initializeUserData();
    } else {
      // Clear user data when logged out
      setProfile(null);
      setCurrentDevice(null);
      setCurrentSession(null);
      setDevices([]);
      setSessions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user]); // Intentionally excluding initializeUserData to prevent infinite loops

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) return;
    
    setIsUpdatingProfile(true);
    try {
      // TODO: Implement API call to update profile
      // const response = await apiService.updateProfile(updates);
      
      // For now, just update local state
      setProfile(prev => prev ? { ...prev, ...updates } : null);
      
      console.log('Profile updated:', updates);
    } catch (error) {
      console.error('Failed to update profile:', error);
      throw error;
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const updatePreferences = async (preferences: Partial<UserProfile['preferences']>) => {
    if (!profile) return;
    
    setIsUpdatingProfile(true);
    try {
      // TODO: Implement API call to update preferences
      // const response = await apiService.updatePreferences(preferences);
      
      // For now, just update local state
      setProfile(prev => prev ? {
        ...prev,
        preferences: { 
          theme: 'light',
          language: 'en',
          notifications: {
            email: true,
            push: true,
            security_alerts: true,
          },
          ...prev.preferences, 
          ...preferences 
        }
      } : null);
      
      console.log('Preferences updated:', preferences);
    } catch (error) {
      console.error('Failed to update preferences:', error);
      throw error;
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const getDevices = async () => {
    if (!user) return;
    
    setIsManagingDevices(true);
    try {
      const response = await apiService.getDevices();
      // Backend already filters to desktop/mobile only via ListDesktopDevices endpoint
      setDevices(response);
      console.log('Devices loaded:', response.length, 'devices');
    } catch (error) {
      console.error('Failed to load devices:', error);
    } finally {
      setIsManagingDevices(false);
    }
  };

  const getSessions = async () => {
    if (!user) return;
    
    setIsManagingDevices(true);
    try {
      const response = await apiService.getSessions();
      setSessions(response);
      console.log('Sessions loaded');
    } catch (error) {
      console.error('Failed to load sessions:', error);
    } finally {
      setIsManagingDevices(false);
    }
  };

  const revokeDevice = async (deviceId: string) => {
    if (!user) return;
    
    setIsManagingDevices(true);
    try {
      await apiService.deleteDevice(deviceId);
      
      // Update local state
      setDevices(prev => prev.filter(device => device.id !== deviceId));
      
      // If revoking current device, log out
      if (currentDevice?.id === deviceId) {
        // This will trigger logout in AuthContext
        window.location.href = '/login';
      }
      
      console.log('Device revoked:', deviceId);
    } catch (error) {
      console.error('Failed to revoke device:', error);
      throw error;
    } finally {
      setIsManagingDevices(false);
    }
  };

  const revokeSession = async (sessionId: string) => {
    if (!user) return;
    
    setIsManagingDevices(true);
    try {
      await apiService.revokeSession(sessionId);
      
      // Update local state
      setSessions(prev => prev.filter(session => session.id !== sessionId));
      
      // If revoking current session, log out
      if (currentSession?.id === sessionId) {
        // This will trigger logout in AuthContext
        window.location.href = '/login';
      }
      
      console.log('Session revoked:', sessionId);
    } catch (error) {
      console.error('Failed to revoke session:', error);
      throw error;
    } finally {
      setIsManagingDevices(false);
    }
  };

  const value: UserContextType = {
    // User Profile
    profile,
    
    // Device & Session Management
    currentDevice,
    currentSession,
    devices,
    sessions,
    
    // User Actions
    updateProfile,
    updatePreferences,
    getDevices,
    getSessions,
    revokeDevice,
    revokeSession,
    
    // Loading States
    isLoading,
    isUpdatingProfile,
    isManagingDevices,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
